import { BaseResourceService } from '../client/base-resource-service.js';
import { API_ENDPOINTS } from '../config/api-config.js';

/**
 * Issues Service (Public Routes Only)
 * Handles public issue-related API endpoints
 */
export class IssuesService extends BaseResourceService {
  constructor(httpClient) {
    super(httpClient, API_ENDPOINTS.ISSUES);
  }

  /**
   * Get all issues with optional filtering (PUBLIC)
   */
  async getAll(params = {}) {
    return this.client.get(this.path, params);
  }

  /**
   * Get a single issue by ID (PUBLIC)
   */
  async getById(id) {
    return this.client.get(`${this.path}/${id}`);
  }

  /**
   * Get issues by status (PUBLIC)
   */
  async getByStatus(status) {
    return this.client.get(`${API_ENDPOINTS.ISSUES_BY_STATUS}/${status}`);
  }

  /**
   * Get customer summary for issues (PUBLIC)
   */
  async getCustomerSummary(customer) {
    return this.client.get(`${API_ENDPOINTS.ISSUES_CUSTOMER_SUMMARY}/${customer}/summary`);
  }

  /**
   * Get issues with filters (PUBLIC)
   */
  async getFiltered(filters = {}) {
    return this.client.get(this.path, filters);
  }

  /**
   * Get issues by email (PUBLIC)
   */
  async getByEmail(email) {
    return this.getFiltered({ email });
  }

  /**
   * Get issues by work type (PUBLIC)
   */
  async getByWorkType(workType) {
    return this.getFiltered({ worktype: workType });
  }

  /**
   * Get issues by date range (PUBLIC)
   */
  async getByDateRange(startDate, endDate) {
    return this.getFiltered({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
  }

  /**
   * Get recent issues (PUBLIC)
   */
  async getRecent(limit = 10) {
    return this.getFiltered({
      limit,
      sort: 'createdAt',
      order: 'desc'
    });
  }

  /**
   * Get paginated results (PUBLIC)
   */
  async getPaginated(page = 1, limit = 10, filters = {}) {
    const params = {
      page,
      limit,
      ...filters
    };
    return this.client.get(this.path, params);
  }

  /**
   * Search issues (PUBLIC)
   */
  async search(query, filters = {}) {
    const params = {
      search: query,
      ...filters
    };
    return this.client.get(this.path, params);
  }

  /**
   * Create new issue (PUBLIC - for citizen submissions)
   */
  async create(issueData) {
    return this.client.post(this.path, issueData);
  }

  /**
   * Update issue (PUBLIC - for citizen updates)
   */
  async update(id, issueData) {
    return this.client.put(`${this.path}/${id}`, issueData);
  }

  /**
   * Partially update issue (PUBLIC)
   */
  async patch(id, partialData) {
    return this.client.patch(`${this.path}/${id}`, partialData);
  }

  /**
   * Delete issue (PUBLIC - for citizen deletions)
   */
  async delete(id) {
    return this.client.delete(`${this.path}/${id}`);
  }

  /**
   * Bulk operations (PUBLIC)
   */
  async bulkUpdate(updates) {
    return this.client.post(`${this.path}/bulk`, { updates });
  }

  // NOTE: The following methods are removed as they require authentication:
  // - getStatistics() - likely requires auth
  // - exportToCsv() - likely requires auth
}